/**
 * contractCoords — SHARED normalized-coordinate math for contract fields.
 *
 * SINGLE SOURCE OF TRUTH (AD-18 / AR-23 / enforcement rule 5). Every contract coordinate
 * computation — editor preview (Epic 2 / AD-15), signing ceremony render (Epic 5), and
 * server-side seal/flatten (Epic 6) — MUST import from here. NEVER hand-roll
 * normalize/denormalize math anywhere else. The Python mirror is
 * `app/services/contracts/contract_coords.py` (joules-admin-backend); the two are kept in
 * lockstep and proven identical by the shared parity fixture (`__fixtures__/coord-parity.json`).
 *
 * THE COORDINATE CONTRACT (must be exact — identical in both modules):
 *   - Normalized floats in [0,1].
 *   - Origin TOP-LEFT.
 *   - page_index is 0-based.
 *   - x, y = field top-left.
 *   - w, h = fraction of page width / height.
 *
 * This module stays origin-top-left throughout. If a PDF consumer needs PDF's
 * bottom-left origin, it flips at the call site — never here.
 *
 * The NormalizedField shape uses snake_case keys (page_index) because it IS the persisted
 * `contract_fields` row / wire-JSON shape (AD-3, enforcement rule 2). Function names are
 * camelCase per frontend convention; the Python mirror uses snake_case names, same math.
 */

/** Persisted normalized field shape (snake_case keys — matches contract_fields row / wire JSON). */
export interface NormalizedField {
  page_index: number; // 0-based
  x: number; // top-left x, normalized [0,1]
  y: number; // top-left y, normalized [0,1]
  w: number; // width as fraction of page width, [0,1]
  h: number; // height as fraction of page height, [0,1]
}

/** Absolute box in the target unit (CSS px for pixel-space, PDF points for point-space). */
export interface AbsoluteBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A rectangle in PDF user space (PDF points, **bottom-left origin** — the convention
 * pdf-lib's `page.drawRectangle({ x, y, width, height })` expects, where `x,y` is the
 * rect's bottom-left corner measured from the page's bottom-left). This is the ONE place
 * the top-left↔bottom-left flip lives (CB-1.8 / AD-18 / rule 5) — seal/flatten consumers
 * must use these helpers, never re-derive the flip.
 */
export interface PdfRect {
  x: number; // bottom-left corner x, PDF points from left
  y: number; // bottom-left corner y, PDF points from bottom
  width: number;
  height: number;
}

function denormalize(f: NormalizedField, pageWidth: number, pageHeight: number): AbsoluteBox {
  return {
    left: f.x * pageWidth,
    top: f.y * pageHeight,
    width: f.w * pageWidth,
    height: f.h * pageHeight,
  };
}

function normalize(
  b: AbsoluteBox,
  pageWidth: number,
  pageHeight: number,
  page_index: number,
): NormalizedField {
  if (pageWidth <= 0 || pageHeight <= 0) {
    throw new Error('contractCoords: page dimensions must be positive');
  }
  return {
    page_index,
    x: b.left / pageWidth,
    y: b.top / pageHeight,
    w: b.width / pageWidth,
    h: b.height / pageHeight,
  };
}

// ── Pixel space (browser/editor — viewport CSS px from pdfjs page.getViewport()) ──

export function denormalizeToPixels(
  f: NormalizedField,
  pageWidthPx: number,
  pageHeightPx: number,
): AbsoluteBox {
  return denormalize(f, pageWidthPx, pageHeightPx);
}

export function normalizeFromPixels(
  b: AbsoluteBox,
  pageWidthPx: number,
  pageHeightPx: number,
  pageIndex: number,
): NormalizedField {
  return normalize(b, pageWidthPx, pageHeightPx, pageIndex);
}

// ── Point space (PDF output — page dims in PDF points) ──

export function denormalizeToPoints(
  f: NormalizedField,
  pageWidthPt: number,
  pageHeightPt: number,
): AbsoluteBox {
  return denormalize(f, pageWidthPt, pageHeightPt);
}

export function normalizeFromPoints(
  b: AbsoluteBox,
  pageWidthPt: number,
  pageHeightPt: number,
  pageIndex: number,
): NormalizedField {
  return normalize(b, pageWidthPt, pageHeightPt, pageIndex);
}

// ── PDF user-space (bottom-left origin — for pdf-lib seal/flatten) ──

/**
 * Convert a top-left normalized field to a pdf-lib rectangle (PDF points, bottom-left origin).
 * The flip: a field whose top edge sits `y` down from the top has its bottom-left corner at
 * `pageHeightPt * (1 - y - h)` from the page bottom. This is the sole owner of that flip.
 */
export function denormalizeToPdfRect(
  f: NormalizedField,
  pageWidthPt: number,
  pageHeightPt: number,
): PdfRect {
  return {
    x: f.x * pageWidthPt,
    y: pageHeightPt * (1 - f.y - f.h),
    width: f.w * pageWidthPt,
    height: f.h * pageHeightPt,
  };
}

/** Inverse of denormalizeToPdfRect: pdf-lib bottom-left rect → top-left normalized field. */
export function normalizeFromPdfRect(
  r: PdfRect,
  pageWidthPt: number,
  pageHeightPt: number,
  pageIndex: number,
): NormalizedField {
  if (pageWidthPt <= 0 || pageHeightPt <= 0) {
    throw new Error('contractCoords: page dimensions must be positive');
  }
  const h = r.height / pageHeightPt;
  return {
    page_index: pageIndex,
    x: r.x / pageWidthPt,
    y: 1 - r.y / pageHeightPt - h,
    w: r.width / pageWidthPt,
    h,
  };
}

// ── Guard ──

/**
 * Clamp a field into the coordinate contract's ranges so out-of-range inputs never produce
 * off-page boxes: each of x,y,w,h is clamped to [0,1] and page_index to a non-negative
 * integer. (Per-axis only — does NOT adjust x+w>1 overflow; that is a placement concern.)
 */
export function clampNormalized(f: NormalizedField): NormalizedField {
  const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
  return {
    page_index: Math.max(0, Math.trunc(f.page_index)),
    x: clamp01(f.x),
    y: clamp01(f.y),
    w: clamp01(f.w),
    h: clamp01(f.h),
  };
}
